/**
 * PTZ control overlay — steer the camera this feed is watching.
 *
 * Mounted as a sibling of the video inside `.video-wrapper`, the same way
 * `TripwireEditor` and `RoiEditor` are. Policy and geometry live in `ptz.ts`, the
 * client twin of `backend/ptz.py`; this file is presentation and I/O only.
 *
 * Two things here are deliberate and worth not "improving":
 *
 *  * **The pad steps, it does not hold.** Glass-to-glass is ~2.5 s, of which ~2.0 s is
 *    the camera's own fixed GOP. A press-and-hold ContinuousMove with that much
 *    feedback delay overshoots every time and the operator chases the picture.
 *  * **No zoom control.** These cameras have a fixed 4 mm lens and report no zoom
 *    axis; a zoom button would be a dead button.
 */
import { Icon } from '../../icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { contentRect, toNormalized, ContentRect } from './roiGeometry';
import {
  DEFAULT_STEP, Direction, REFUSAL, bindingsStale, nudgeFor, pixelToOffset, validateStep,
} from './ptz';

const FALLBACK_FRAME = { width: 640, height: 360 };
/** Long enough for the mechanical settle (measured 0.6-1.9 s) plus the video lag. */
const REFRESH_AFTER_MOVE_MS = 2800;

const STEPS: Array<{ label: string; value: number; title: string }> = [
  { label: 'FINE', value: 0.02, title: 'about 3.5 degrees of pan' },
  { label: 'STEP', value: DEFAULT_STEP, title: 'about 8.7 degrees of pan' },
  { label: 'WIDE', value: 0.15, title: 'about 26 degrees of pan' },
];

const PAD: Array<Direction | 'stop' | null> = [
  'up-left', 'up', 'up-right',
  'left', 'stop', 'right',
  'down-left', 'down', 'down-right',
];

const ARROW: Record<string, string> = {
  'up-left': '↖', up: '↑', 'up-right': '↗',
  left: '←', stop: '■', right: '→',
  'down-left': '↙', down: '↓', 'down-right': '↘',
};

interface Preset { id: number; name: string; pan: number | null; tilt: number | null; }

interface TrackSession { until: number; remaining_s: number; armed_by?: string | null; }

interface FeedInfo {
  enabled: boolean;
  steerable: boolean;
  reason?: string;
  stream_key?: string;
  camera?: { id: string; label: string };
  position?: { x: number | null; y: number | null; moving: boolean; error: string | null } | null;
  presets?: Preset[];
  active_preset_id?: number | null;
  track_enabled?: boolean;
  tracking?: TrackSession | null;
}

export interface PtzControlProps {
  wrapperRef: React.RefObject<HTMLDivElement>;
  apiUrl: string;
  authHeaders: (contentType?: boolean) => HeadersInit;
  onClose: () => void;
  /** Lets the panel badge its button and warn that this feed's area/lines/calibration
   *  no longer describe what the lens sees. */
  onStaleChange?: (stale: boolean) => void;
}

export default function PtzControl({
  wrapperRef, apiUrl, authHeaders, onClose, onStaleChange,
}: PtzControlProps) {
  const [info, setInfo] = useState<FeedInfo | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(DEFAULT_STEP);
  const [aiming, setAiming] = useState(false);
  const [savingName, setSavingName] = useState<string | null>(null);
  const [rect, setRect] = useState<ContentRect>({ left: 0, top: 0, width: 0, height: 0 });
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- keep the click-to-aim surface glued to the picture ---------------- //
  const measure = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const img = wrapper.querySelector('img.video-stream') as HTMLImageElement | null;
    setRect(contentRect(
      wrapper.clientWidth, wrapper.clientHeight,
      img?.naturalWidth || FALLBACK_FRAME.width,
      img?.naturalHeight || FALLBACK_FRAME.height,
    ));
  }, [wrapperRef]);

  useEffect(() => {
    measure();
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const ro = new ResizeObserver(measure);
    ro.observe(wrapper);
    const poll = setInterval(measure, 1000);
    return () => { ro.disconnect(); clearInterval(poll); };
  }, [measure, wrapperRef]);

  // --- feed state --------------------------------------------------------- //
  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/ptz/feed`, { headers: authHeaders() });
      if (!res.ok) { setInfo({ enabled: false, steerable: false, reason: REFUSAL.disabled }); return; }
      setInfo(await res.json());
    } catch {
      setInfo({ enabled: true, steerable: false, reason: 'Could not reach the server' });
    }
  }, [apiUrl, authHeaders]);

  useEffect(() => { refresh(); }, [refresh]);

  // A camera can also be moved from the VIGI phone app or by a second operator, so the
  // panel keeps asking rather than trusting its own last action.
  useEffect(() => {
    const poll = setInterval(refresh, 10000);
    return () => clearInterval(poll);
  }, [refresh]);

  const presets = info?.presets ?? [];
  const stale = bindingsStale(presets, info?.active_preset_id);

  useEffect(() => { onStaleChange?.(stale); }, [stale, onStaleChange]);
  useEffect(() => () => { onStaleChange?.(false); }, [onStaleChange]);
  useEffect(() => () => { if (refreshTimer.current) clearTimeout(refreshTimer.current); }, []);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(refresh, REFRESH_AFTER_MOVE_MS);
  }, [refresh]);

  const call = useCallback(async (
    path: string, body: unknown, method = 'POST',
  ): Promise<boolean> => {
    setBusy(true);
    setStatus(null);
    try {
      const res = await fetch(`${apiUrl}${path}`, {
        method,
        headers: authHeaders(true),
        body: method === 'DELETE' ? undefined : JSON.stringify(body ?? {}),
      });
      if (!res.ok) {
        // The server's refusals are written to be read by a person — "camera is at its
        // tilt limit", "4 of 4 presets in use" — so show them rather than a code.
        const detail = await res.json().catch(() => null);
        setStatus(detail?.detail || `Request failed (${res.status})`);
        return false;
      }
      return true;
    } catch {
      setStatus('Could not reach the server');
      return false;
    } finally {
      setBusy(false);
    }
  }, [apiUrl, authHeaders]);

  const move = useCallback(async (dir: Direction | 'stop') => {
    if (dir === 'stop') {
      await call('/api/ptz/stop', {});
      scheduleRefresh();
      return;
    }
    const [dx, dy] = nudgeFor(dir, step);
    const refusal = validateStep(dx, dy);
    if (refusal) { setStatus(refusal); return; }
    if (await call('/api/ptz/nudge', { dx, dy })) {
      // Optimistic: the server has already dropped the active preset, so reflect that
      // now rather than leaving the area looking trustworthy for another poll.
      setInfo((prev) => (prev ? { ...prev, active_preset_id: null } : prev));
      scheduleRefresh();
    }
  }, [call, scheduleRefresh, step]);

  const aimAt = useCallback(async (event: React.MouseEvent<HTMLDivElement>) => {
    const wrapper = wrapperRef.current;
    if (!wrapper || rect.width <= 0) return;
    const box = wrapper.getBoundingClientRect();
    const [u, v] = toNormalized(event.clientX - box.left, event.clientY - box.top, rect);
    const [dx, dy] = pixelToOffset(u, v);
    if (validateStep(dx, dy)) { setStatus('Already centred there'); return; }
    if (await call('/api/ptz/center', { u, v })) {
      setInfo((prev) => (prev ? { ...prev, active_preset_id: null } : prev));
      setStatus('Aiming — a second click will fine-tune it');
      scheduleRefresh();
    }
  }, [call, rect, scheduleRefresh, wrapperRef]);

  const savePreset = useCallback(async () => {
    const name = (savingName || '').trim();
    if (!name) return;
    if (await call('/api/ptz/presets', { name })) {
      setSavingName(null);
      await refresh();
    }
  }, [call, refresh, savingName]);

  const gotoPreset = useCallback(async (preset: Preset) => {
    if (await call(`/api/ptz/presets/${preset.id}/goto`, {})) {
      setInfo((prev) => (prev ? { ...prev, active_preset_id: preset.id } : prev));
      setStatus(`Recalling ${preset.name}`);
      scheduleRefresh();
    }
  }, [call, scheduleRefresh]);

  const deletePreset = useCallback(async (preset: Preset) => {
    if (await call(`/api/ptz/presets/${preset.id}`, null, 'DELETE')) await refresh();
  }, [call, refresh]);

  const toggleTrack = useCallback(async () => {
    const enable = !info?.tracking;
    if (await call('/api/ptz/track', { enable })) {
      setStatus(enable
        ? 'Following the largest person in frame — it stands down on its own'
        : 'Auto-track stood down');
      await refresh();
    }
  }, [call, info?.tracking, refresh]);

  // --- render ------------------------------------------------------------- //

  const cardStyle: React.CSSProperties = {
    position: 'absolute', bottom: 8, left: 8, zIndex: 6,
    background: 'rgba(12,16,22,0.9)', border: '1px solid #2b3442', borderRadius: 6,
    padding: 10, color: '#dfe7f2', font: '11px/1.4 system-ui, sans-serif',
    minWidth: 214, maxWidth: 280,
  };
  const btn: React.CSSProperties = {
    background: '#1a212b', border: '1px solid #2b3442', color: '#dfe7f2',
    borderRadius: 4, cursor: 'pointer', padding: '4px 6px', font: 'inherit',
  };

  if (info && !info.steerable) {
    return (
      <div style={cardStyle}>
        <Header label="PTZ" onClose={onClose} />
        <div style={{ color: '#9aa7b8', marginTop: 6 }}>{info.reason || REFUSAL.noCamera}</div>
      </div>
    );
  }

  return (
    <>
      {aiming && rect.width > 0 && (
        <div
          onClick={aimAt}
          title="Click a point to bring it to the centre"
          style={{
            position: 'absolute', left: rect.left, top: rect.top,
            width: rect.width, height: rect.height, zIndex: 5,
            cursor: 'crosshair', background: 'rgba(120,220,255,0.06)',
            border: '1px dashed rgba(120,220,255,0.5)',
          }}
        />
      )}
      <div style={cardStyle}>
        <Header label={info?.camera?.label || 'PTZ'} onClose={onClose} />

        {stale && (
          <div style={{
            marginTop: 6, padding: '4px 6px', borderRadius: 4,
            background: 'rgba(255,209,102,0.14)', border: '1px solid var(--color-warning)', color: 'var(--color-warning)',
          }}>
            Off preset — this camera&apos;s detection area, counting lines and calibration
            do not describe what it is looking at now.
          </div>
        )}

        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 34px)', gap: 3, marginTop: 8,
        }}>
          {PAD.map((dir) => (
            <button
              key={dir as string}
              style={{ ...btn, height: 30, opacity: busy ? 0.5 : 1 }}
              disabled={busy}
              onClick={() => move(dir as Direction | 'stop')}
              title={dir === 'stop' ? 'Stop the camera' : `Nudge ${dir}`}
            >
              {ARROW[dir as string]}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 3, marginTop: 8 }}>
          {STEPS.map((s) => (
            <button
              key={s.label}
              onClick={() => setStep(s.value)}
              title={s.title}
              style={{
                ...btn, flex: 1,
                ...(step === s.value
                  ? { background: '#78dcff', borderColor: 'var(--color-info)', color: '#101318' }
                  : {}),
              }}
            >
              {s.label}
            </button>
          ))}
        </div>

        <button
          onClick={() => setAiming((v) => !v)}
          style={{
            ...btn, width: '100%', marginTop: 6,
            ...(aiming ? { background: '#78dcff', borderColor: 'var(--color-info)', color: '#101318' } : {}),
          }}
          title="Click a point in the video to bring it to the centre"
        >
          {aiming ? 'CLICK THE VIDEO — TAP TO EXIT' : 'CLICK TO AIM'}
        </button>

        {info?.track_enabled && (
          <button
            onClick={toggleTrack}
            disabled={busy}
            style={{
              ...btn, width: '100%', marginTop: 6,
              ...(info?.tracking
                ? { background: '#f4776a', borderColor: '#f4776a', color: '#101318' }
                : {}),
            }}
            title={info?.tracking
              ? 'Stop following and return to the view this was armed from'
              : 'Follow the largest person in frame. Needs an AI layer on this feed, and it always expires.'}
          >
            {info?.tracking
              ? `TRACKING — STOP (${Math.round(info.tracking.remaining_s)}s)`
              : 'AUTO-TRACK'}
          </button>
        )}
        {info?.tracking && (
          <div style={{ marginTop: 5, color: '#f4776a' }}>
            The camera is moving on its own, so this feed&apos;s area, lines and
            calibration stay stale until it stands down.
          </div>
        )}

        <div style={{ marginTop: 10, borderTop: '1px solid #2b3442', paddingTop: 8 }}>
          <div style={{ color: '#9aa7b8', marginBottom: 5 }}>
            SAVED VIEWS{presets.length ? ` (${presets.length})` : ''}
          </div>
          {presets.length === 0 && (
            <div style={{ color: '#66717f', marginBottom: 6 }}>
              None yet. Saving one binds this camera&apos;s detection area and counting
              lines to the view they were drawn on.
            </div>
          )}
          {presets.map((p) => (
            <div key={p.id} style={{ display: 'flex', gap: 3, marginBottom: 3 }}>
              <button
                onClick={() => gotoPreset(p)}
                disabled={busy}
                style={{
                  ...btn, flex: 1, textAlign: 'left',
                  ...(info?.active_preset_id === p.id
                    ? { background: 'var(--color-accent)', borderColor: 'var(--color-accent)', color: '#101318' }
                    : {}),
                }}
                title={`Recall ${p.name}`}
              >
                {p.name}
              </button>
              <button
                onClick={() => deletePreset(p)}
                disabled={busy}
                style={{ ...btn, color: '#f47', width: 26 }}
                title={`Delete ${p.name} — also deletes the detection area and lines bound to it`}
                aria-label="Remove"
              >
                <Icon name="clear" />
              </button>
            </div>
          ))}
          {savingName === null ? (
            <button onClick={() => setSavingName('')} style={{ ...btn, width: '100%' }}>
              + SAVE THIS VIEW
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 3 }}>
              <input
                autoFocus
                value={savingName}
                maxLength={64}
                placeholder="Gate, Yard, Forecourt…"
                onChange={(e) => setSavingName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') savePreset();
                  if (e.key === 'Escape') setSavingName(null);
                }}
                style={{
                  ...btn, flex: 1, cursor: 'text', background: '#0c1016',
                }}
              />
              <button onClick={savePreset} disabled={busy || !savingName.trim()} style={btn}>
                OK
              </button>
              <button onClick={() => setSavingName(null)} style={btn} aria-label="Close"><Icon name="clear" /></button>
            </div>
          )}
        </div>

        {status && (
          <div style={{ marginTop: 8, color: 'var(--color-warning)' }}>{status}</div>
        )}
        {info?.position?.error && (
          <div style={{ marginTop: 6, color: '#f47' }}>{info.position.error}</div>
        )}
      </div>
    </>
  );
}

function Header({ label, onClose }: { label: string; onClose: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <strong style={{ letterSpacing: 0.5 }}>{label.toUpperCase()}</strong>
      <button
        onClick={onClose}
        title="Close"
        style={{
          background: 'none', border: 'none', color: '#9aa7b8',
          cursor: 'pointer', font: 'inherit', fontSize: 14, lineHeight: 1, padding: 0,
        }}
        aria-label="Close"
      >
        <Icon name="clear" />
      </button>
    </div>
  );
}
